import { SortTh } from '../../components/SortTh';
import { ExpandRow, Summary } from '../../components/ExpandRow';
import { Link, useSearchParams } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { formatCents, formatQuantityMilli, PRODUCT_KIND_LABELS, PRODUCT_KINDS, type Paginated, type Product } from '@kontor/shared';
import { api, queryString } from '../../api';
import { FilterBar, type FilterField } from '../../components/filter/FilterBar';
import { toneClass } from '../../components/color/ColorSwatches';
import { useSession } from '../../session';
import { imageUrl, useProductLookups } from './lookups';
import './products.css';

/** Lagerbestand und Verfügbarkeit für die Liste, nur bei Produkten im Lager */
const stockText = (p: Product) => (p.stockMilli === null ? null : p.stockMilli > 0 ? `${formatQuantityMilli(p.stockMilli)} ${p.unitSymbol ?? ''} auf Lager`.trim() : 'nicht verfügbar');

const STATUS_LABELS = { active: 'Aktive', inactive: 'Archivierte', all: 'Alle' } as const;

/** Liste der Produkte mit Bild, Suche und Filtern (KON-108). Suche, Filter und Seite stehen in der Adresszeile. */
export function ProductsPage() {
  const [params, setParams] = useSearchParams();
  const { can } = useSession();
  const lookups = useProductLookups();
  const q = params.get('q') ?? '';
  const categoryId = params.get('kategorie') ?? '';
  const kind = params.get('art') ?? '';
  const tagId = params.get('tag') ?? '';
  const status = params.get('status') ?? 'active';
  const sellable = params.get('verkauf') ?? '';
  const sort = params.get('sort') ?? 'name';
  const page = Number(params.get('page') ?? 1);

  const list = useQuery({
    queryKey: ['products', { q, categoryId, kind, tagId, status, sellable, sort, page }],
    queryFn: () => api<Paginated<Product>>('GET', `/products${queryString({ q, categoryId, kind, tagId, status, sellable, sort, page, perPage: 25 })}`),
    placeholderData: keepPreviousData,
  });
  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v) merged.set(k, v);
      else merged.delete(k);
    }
    if (!('page' in next)) merged.delete('page');
    setParams(merged);
  };
  const fields: FilterField[] = [
    { key: 'kategorie', label: 'Kategorie', type: 'select', options: [{ value: '', label: 'Alle' }, ...lookups.categories.map((c) => ({ value: c.id, label: c.parentId ? `– ${lookups.categoryLabel(c.id)}` : c.name }))] },
    { key: 'art', label: 'Art', type: 'select', options: [{ value: '', label: 'Alle' }, ...PRODUCT_KINDS.map((k) => ({ value: k, label: PRODUCT_KIND_LABELS[k] }))] },
    ...(lookups.tags.length ? [{ key: 'tag', label: 'Tag', type: 'select' as const, options: [{ value: '', label: 'Alle' }, ...lookups.tags.map((t) => ({ value: t.id, label: t.name }))] }] : []),
    { key: 'verkauf', label: 'Verkauf', type: 'select', options: [{ value: '', label: 'Alle' }, { value: 'yes', label: 'Wird verkauft' }, { value: 'no', label: 'Nicht verkauft (Verbrauchsmaterial)' }] },
    { key: 'status', label: 'Status', type: 'select', options: (['active', 'inactive', 'all'] as const).map((s) => ({ value: s === 'active' ? '' : s, label: STATUS_LABELS[s] })) },
    {
      key: 'sort',
      label: 'Sortierung',
      type: 'select',
      options: [
        { value: '', label: 'Name A–Z' },
        { value: '-name', label: 'Name Z–A' },
        { value: 'sku', label: 'Artikelnummer' },
        { value: 'price', label: 'Preis aufsteigend' },
        { value: '-price', label: 'Preis absteigend' },
        { value: '-createdAt', label: 'Neueste zuerst' },
      ],
    },
  ];
  const onSort = (next: string) => update({ sort: next === 'name' ? '' : next });
  const filtered = !!(q || categoryId || kind || tagId || sellable || status !== 'active');

  return (
    <>
      <div className="page-head">
        <h1>Produkte</h1>
        <span className="muted">{list.data ? `${list.data.meta.total} ${status === 'inactive' ? 'archiviert' : status === 'all' ? 'insgesamt' : 'aktiv'}` : ''}</span>
        {can('products.write') && <Link className="btn btn--primary btn--sm page-head__action" to="/produkte/neu">Neues Produkt</Link>}
      </div>
      <FilterBar
        search={{ value: q, placeholder: 'Name, Artikelnummer, EAN oder Hersteller', onSubmit: (v) => update({ q: v }) }}
        fields={fields}
        values={{ kategorie: categoryId, art: kind, tag: tagId, status: status === 'active' ? '' : status, sort: sort === 'name' ? '' : sort }}
        onChange={(key, value) => update({ [key]: value })}
        onReset={() => setParams(new URLSearchParams())}
      />
      {list.isError && <div className="alert alert--error">{(list.error as Error).message}</div>}
      <div className="card table-card">
        <table className="table table--cards product-table">
          <thead>
            <tr>
              <th><span className="sr-only">Bild</span></th>
              <SortTh label="Produkt" sortKey="name" sort={sort} onSort={onSort} />
              <th>Kategorie</th>
              <SortTh label="Art" sortKey="kind" sort={sort} onSort={onSort} />
              <th>Tags</th>
              <SortTh label="Preis netto" sortKey="price" sort={sort} onSort={onSort} className="num" />
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <tr><td colSpan={6} className="muted">Wird geladen …</td></tr>}
            {list.data?.data.length === 0 && <tr><td colSpan={6} className="empty">{filtered ? 'Keine Produkte zu dieser Suche.' : 'Noch keine Produkte. Legen Sie das erste über „Neues Produkt“ an.'}</td></tr>}
            {list.data?.data.map((p) => (
              <ExpandRow
                key={p.id}
                className={p.isActive ? undefined : 'row--muted'}
                summary={
                  <div className="product-sum">
                    {p.images[0] ? <img src={imageUrl(p.id, p.images[0].id)} alt="" loading="lazy" /> : <span className="product-thumb--empty" aria-hidden="true" />}
                    <Summary
                      title={<Link to={`/produkte/${p.id}`} className="strong-link">{p.name}</Link>}
                      aside={<span className="num">{formatCents(p.salePriceCents)}</span>}
                      line2={[PRODUCT_KIND_LABELS[p.kind], lookups.categoryLabel(p.categoryId)].filter(Boolean).join(' · ')}
                      line3={[p.sku, p.isActive ? null : 'archiviert', p.isSellable ? null : 'nicht verkauft', stockText(p)].filter(Boolean).join(' · ') || undefined}
                    />
                  </div>
                }
              >
                <td className="product-table__thumb cell-desktop-title">
                  {p.images[0] ? <img src={imageUrl(p.id, p.images[0].id)} alt="" loading="lazy" /> : <span className="product-thumb--empty" aria-hidden="true" />}
                </td>
                <td className="cell-desktop-title">
                  <Link to={`/produkte/${p.id}`} className="strong-link">{p.name}</Link>
                  <div className="muted small">{[p.sku, p.isActive ? null : 'archiviert', p.isSellable ? null : 'nicht verkauft', stockText(p)].filter(Boolean).join(' · ')}</div>
                </td>
                <td data-label="Kategorie">{lookups.categoryLabel(p.categoryId) ?? <span className="muted">–</span>}</td>
                <td data-label="Art">{PRODUCT_KIND_LABELS[p.kind]}</td>
                <td data-label="Tags">
                  {p.tagIds.length ? (
                    <span className="tag-badges">
                      {p.tagIds.map((id) => lookups.tags.find((t) => t.id === id)).filter(Boolean).map((t) => <span key={t!.id} className={`tag-badge ${toneClass(t!.color)}`}>{t!.name}</span>)}
                    </span>
                  ) : <span className="muted">–</span>}
                </td>
                <td data-label="Preis netto" className="num"><span>{formatCents(p.salePriceCents)}{p.unitSymbol ? <span className="muted small"> / {p.unitSymbol}</span> : null}</span></td>
                <td className="cell-mobile-only cell-actions"><Link className="btn btn--secondary btn--sm" to={`/produkte/${p.id}`}>Produkt öffnen und bearbeiten</Link></td>
              </ExpandRow>
            ))}
          </tbody>
        </table>
      </div>
      {list.data && list.data.meta.lastPage > 1 && (
        <nav className="pager" aria-label="Seiten">
          <button type="button" className="btn btn--secondary btn--sm" disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>Zurück</button>
          <span className="muted">Seite {page} von {list.data.meta.lastPage}</span>
          <button type="button" className="btn btn--secondary btn--sm" disabled={page >= list.data.meta.lastPage} onClick={() => update({ page: String(page + 1) })}>Weiter</button>
        </nav>
      )}
    </>
  );
}
